import { useEffect, useState } from "react";

import { useToast } from "~/hooks/use-toast";
import { OrderItem, Product } from "~/types/product";
import { calcLineTotal } from "~/utils/lineItemCalculations";

// Extend OrderItem interface to include suggested price and price range info
interface ExtendedOrderItem extends OrderItem {
  suggestedPrice: number;
  hasPriceRanges: boolean;
  discountPercent: number;
}

export const useOrderItems = () => {
  const [orderItems, setOrderItems] = useState<ExtendedOrderItem[]>([]);
  const [total, setTotal] = useState(0);
  const [editingItem, setEditingItem] = useState<ExtendedOrderItem | null>(
    null
  );
  const [hasPriceBelowSuggested, setHasPriceBelowSuggested] = useState(false);
  const { toast } = useToast();

  // Calculate total whenever order items change
  useEffect(() => {
    const newTotal = orderItems.reduce((sum, item) => {
      return (
        sum +
        calcLineTotal(item.unitPrice, item.quantity, item.discountPercent ?? 0)
      );
    }, 0);
    setTotal(newTotal);
  }, [orderItems]);

  // Check if any items have prices below suggested
  useEffect(() => {
    const belowSuggested = orderItems.some(
      (item) => item.unitPrice < item.suggestedPrice
    );
    setHasPriceBelowSuggested(belowSuggested);
  }, [orderItems]);

  // Handle adding a product to the order
  const handleAddProduct = (
    product: Product,
    quantity: number,
    unitPrice: number,
    suggestedPrice: number,
    hasPriceRanges: boolean,
    discountPercent: number = 0
  ) => {
    if (editingItem && editingItem.id) {
      // Update existing item
      const updatedItems = orderItems.map((item) =>
        item.id === editingItem.id
          ? {
              ...item,
              product,
              quantity,
              unitPrice,
              suggestedPrice,
              hasPriceRanges,
              discountPercent,
            }
          : item
      );
      setOrderItems(updatedItems);

      toast({
        title: "Producto actualizado",
        description: `${product.short_name} actualizado en la orden`,
      });
    } else {
      // Add new item - allow duplicates with different prices/quantities
      // Generate a unique ID for this item instance
      const newItem = {
        id: crypto.randomUUID(), // Generate unique ID for this item instance
        product,
        quantity,
        unitPrice,
        suggestedPrice,
        hasPriceRanges,
        discountPercent,
      };

      setOrderItems([...orderItems, newItem]);

      toast({
        title: "Producto agregado",
        description: `${product.short_name} agregado a la orden`,
      });
    }

    // Clear editing state
    setEditingItem(null);
  };

  // Handle editing an item in the order
  const handleEditItem = (item: ExtendedOrderItem) => {
    setEditingItem(item);
  };

  // Handle removing an item from the order
  const handleRemoveItem = (itemId: string) => {
    setOrderItems(orderItems.filter((item) => item.id !== itemId));

    // If removing the item that's being edited, clear editing state
    if (editingItem && editingItem.id === itemId) {
      setEditingItem(null);
    }
  };

  return {
    orderItems,
    setOrderItems,
    total,
    editingItem,
    setEditingItem,
    handleAddProduct,
    handleEditItem,
    handleRemoveItem,
    hasPriceBelowSuggested,
  };
};
